/**
 * Page Detection Service - Detects current page from URL
 *
 * Used to conditionally render dev utilities based on the current page.
 */

import { PROJECT_ROUTES } from '~/pages/project/projectRoutes';

export type DevUtilityPage =
  | 'project-setup' // /projects/:id/setup
  | 'existing-debt' // /projects/:id/existing-debt
  | 'proposal' // /projects/:id/proposal-development
  | 'overview' // /projects/:id/overview
  | 'review' // /projects/:id/review
  | 'other'; // Any other page

/**
 * Detects the current page from the URL pathname
 */
export const detectCurrentPage = (pathname: string): DevUtilityPage => {
  // Match project routes: /projects/:id/:route
  const projectRouteMatch = pathname.match(/^\/projects\/[^/]+\/(.+)$/);

  if (projectRouteMatch) {
    const route = projectRouteMatch[1];

    // Match route paths (PROJECT_ROUTES values)
    if (route === PROJECT_ROUTES.SETUP || route === 'setup') {
      return 'project-setup';
    }
    if (route === PROJECT_ROUTES.EXISTING_DEBT || route === 'existing-debt') {
      return 'existing-debt';
    }
    if (
      route === PROJECT_ROUTES.PROPOSAL ||
      route === 'proposal-development' ||
      route === 'proposal'
    ) {
      return 'proposal';
    }
    if (route === PROJECT_ROUTES.OVERVIEW || route === 'project-overview' || route === 'overview') {
      return 'overview';
    }
    if (
      route === PROJECT_ROUTES.PROJECT_REVIEW ||
      route === 'project-review' ||
      route === 'review'
    ) {
      return 'review';
    }
  }

  return 'other';
};

/**
 * Configuration for which utilities should be available on which pages
 */
export interface UtilityPageConfig {
  /** Page identifier */
  page: DevUtilityPage;
  /** Display name for the page */
  displayName: string;
  /** Whether to show Lakeview quick fill utilities */
  showLakeviewQuickFill: boolean;
  /** Whether to show existing debt quick fill utilities */
  showExistingDebtQuickFill: boolean;
  /** Whether to show developer actions (Copy as Testing, Values Testing, Debug Inspector) */
  showDeveloperActions: boolean;
}

/**
 * Default configuration for utility availability by page
 */
export const UTILITY_PAGE_CONFIG: UtilityPageConfig[] = [
  {
    page: 'project-setup',
    displayName: 'Project Setup',
    showLakeviewQuickFill: true,
    showExistingDebtQuickFill: false,
    showDeveloperActions: false,
  },
  {
    page: 'existing-debt',
    displayName: 'Existing Debt',
    showLakeviewQuickFill: false,
    showExistingDebtQuickFill: true,
    showDeveloperActions: false,
  },
  {
    page: 'proposal',
    displayName: 'Proposal Development',
    showLakeviewQuickFill: false,
    showExistingDebtQuickFill: false,
    showDeveloperActions: true,
  },
  {
    page: 'overview',
    displayName: 'Project Overview',
    showLakeviewQuickFill: false,
    showExistingDebtQuickFill: false,
    showDeveloperActions: false,
  },
  {
    page: 'review',
    displayName: 'Project Review',
    showLakeviewQuickFill: false,
    showExistingDebtQuickFill: false,
    showDeveloperActions: false,
  },
  {
    page: 'other',
    displayName: 'Other',
    showLakeviewQuickFill: false,
    showExistingDebtQuickFill: false,
    showDeveloperActions: false,
  },
];

/**
 * Get utility configuration for a specific page
 */
export const getUtilityConfigForPage = (page: DevUtilityPage): UtilityPageConfig => {
  return (
    UTILITY_PAGE_CONFIG.find((config) => config.page === page) ||
    UTILITY_PAGE_CONFIG.find((config) => config.page === 'other')!
  );
};
